/**
 * Compatibility export boundary. The historical name is retained because all
 * exporters import it, but eligibility now delegates to the central V2
 * delivery decision: degraded, metadata-only and failed-schema sources stay
 * barred. A lazy or failed PDF still permits an exact-source artifact request.
 * Downloading bytes uses the separate per-artifact validation. Fully loaded legacy
 * reports retain an explicit conversion-and-validation path.
 */
import { type GenerationTier } from '../../config/generationTier';
import { getSectionContractsForType } from '../../config/reportSections';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import type { ReportRevisionReferenceV1 } from '../../types/reportContractsV2';
import { buildReportDocumentModelV1 } from '../reportGeneration/buildReportDocumentV1';
import {
  evaluateReportDelivery,
  canBuildReportArtifacts,
  type ReportBodyState,
} from '../reportGeneration/evaluateReportDelivery';
import { isWithheldSectionOmissionDetail, withheldNarrativeLeaves } from '../report/withheldNarrative';

export type ExportFormat = 'pdf' | 'docx' | 'pptx' | 'csv';

/** Thrown when an export is attempted on any non-deliverable report. */
export class DraftExportBlockedError extends Error {
  readonly name = 'DraftExportBlockedError';
  readonly format: ExportFormat;
  constructor(format: ExportFormat, reason?: string) {
    super(
      `This ${format.toUpperCase()} cannot be exported. ${reason ?? 'The report is not a validated delivery artifact.'}`,
    );
    this.format = format;
  }
}

/** Hydrated bodies carry source-bound validation; metadata remains fail-closed. */
export type ExportTierGate = Partial<SiteRagReport> & {
  generationTier?: GenerationTier;
  reportRevision?: ReportRevisionReferenceV1;
};

/** Prove that a legacy body can be converted without losing a required
 * section. A summary string is never evidence of a complete report. */
export function hasCompleteLegacyExportConversion(report: ExportTierGate): boolean {
  if (report.reportRevision || report._metadataOnly) return false;
  if (
    typeof report.id !== 'string' || !report.id.trim()
    || typeof report.reportType !== 'string'
    || typeof report.siteName !== 'string' || !report.siteName.trim()
    || typeof report.address !== 'string'
    || typeof report.createdAt !== 'string' || !report.createdAt.trim()
    || typeof report.summary !== 'string' || !report.summary.trim()
  ) return false;
  try {
    const document = buildReportDocumentModelV1(report as SiteRagReport);
    const reportType = report.reportType as ReportTypeId;
    if (!Object.hasOwn(REPORT_TYPE_REGISTRY, reportType)) return false;
    const sectionsById = new Map(document.sections.map((section) => [section.sectionId, section]));
    // A required section whose ONLY content was a withheld narrative leaf is
    // an explicit omission with the withheld detail. Under the 2026-09-18
    // owner decision a withheld leaf holds the report but never bars export,
    // so that section counts as accounted for — exactly as
    // evaluateReportDelivery treats the same omission on a V2 revision. It is
    // exempt only while the body carries the withheld record.
    const withheldRecorded = withheldNarrativeLeaves(report).length > 0;
    const withheldOmittedSectionIds = new Set(document.omissions
      .filter((omission) => withheldRecorded && isWithheldSectionOmissionDetail(omission.detail))
      .map((omission) => omission.sectionId));
    const sectionAccountedFor = (section: { sectionId: string; blocks: readonly unknown[] }) => (
      section.blocks.length > 0 || withheldOmittedSectionIds.has(section.sectionId)
    );
    const everyRequiredRegistrySectionIsComplete = getSectionContractsForType(reportType)
      .filter((section) => section.sectionId !== 'toc' && section.required)
      .every((contract) => {
        const section = sectionsById.get(contract.sectionId);
        return Boolean(section?.required && sectionAccountedFor(section));
      });
    // 'held' is audit-advisory (2026-08-22): a held legacy body exports with
    // the audit-recommended disclosure, exactly like a held V2 revision.
    return (document.qualityDecision.state === 'pass' || document.qualityDecision.state === 'held')
      && document.qualityDecision.hardGatesPassed
      && everyRequiredRegistrySectionIsComplete
      && document.sections.every((section) => !section.required || sectionAccountedFor(section))
      && document.omissions.every((omission) => (
        !omission.required || withheldOmittedSectionIds.has(omission.sectionId)
      ));
  } catch {
    return false;
  }
}

/**
 * The historical `/print/:id` route is a mutable DOM compatibility surface.
 * Immutable V2 reports must be viewed/downloaded from their validated artifact
 * bytes, so the server refuses to hydrate them for that route even when their
 * delivery decision is otherwise exportable.
 */
export function assertMutablePrintEligible(report: ExportTierGate | null | undefined): void {
  if (report?.reportRevision) {
    throw new DraftExportBlockedError(
      'pdf',
      'Immutable report revisions must use their validated PDF artifact; mutable print rendering is disabled.',
    );
  }
  assertExportableTier(report, 'pdf');
}

/**
 * Pure predicate for UI affordances. The legacy path is named at this boundary;
 * every new API must require an immutable V2 artifact.
 */
export function isExportableTier(report: ExportTierGate | null | undefined): boolean {
  if (!report) return false;

  const hasFullLegacyBody = !report.reportRevision && hasCompleteLegacyExportConversion(report);
  const hasHydratedV2Body = Boolean(report.reportRevision)
    && typeof report.summary === 'string'
    && !report._metadataOnly;
  const bodyState: ReportBodyState = report._metadataOnly
    ? 'metadata-only'
    : hasFullLegacyBody || hasHydratedV2Body
      ? 'ready'
      : 'body-unavailable';
  const decision = evaluateReportDelivery({
    report: {
      // Deterministic screening validates the exact body, source identity and
      // scan receipt. A hand-picked metadata subset cannot prove that receipt.
      ...report,
      id: report.id ?? 'legacy-report',
      summary: report.summary ?? '',
    },
    bodyState,
    // Legacy exporters perform their conversion/layout checks synchronously.
    // Naming this policy at the compatibility boundary keeps every new V2 API
    // fail-closed while allowing a genuinely full historical report to pass.
    legacyArtifactPolicy: report.reportRevision
      ? 'require-v2-artifact'
      : 'allow-validated-full-body',
  });
  return decision.canExport || canBuildReportArtifacts(report.pdfDelivery, decision);
}

/**
 * Throw {@link DraftExportBlockedError} when central delivery policy bars the
 * report. Call at the top of every export entry point.
 */
export function assertExportableTier(
  report: ExportTierGate | null | undefined,
  format: ExportFormat,
): void {
  if (!isExportableTier(report)) {
    // A withheld narrative leaf is never the reason (owner decision
    // 2026-09-18: it holds the report, it does not bar export), so it is not
    // named here — a barred report is barred by one of the states below.
    const reason = !report
      ? 'The report body is unavailable.'
      : report._metadataOnly
        ? 'Only metadata is available; load or regenerate the full report body first.'
        : report.degradedMode
          ? 'Degraded reports are view-only.'
          : report.generationTier === 'draft'
            ? 'This legacy draft is view-only; regenerate it at investment-grade quality.'
            : report.isDraft
              ? 'The report is not a validated delivery artifact; review its quality status and regenerate it.'
              : 'The report revision or layout artifact did not pass validation.';
    throw new DraftExportBlockedError(format, reason);
  }
}
